import { ListChecks, ChevronRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChecklistSummary } from '../types';
import { calculateProgress } from '../utils/helpers';

interface ChecklistSidebarProps {
  checklists: ChecklistSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreateClick: () => void;
  isLoading?: boolean;
}

export function ChecklistSidebar({
  checklists,
  selectedId,
  onSelect,
  onCreateClick,
  isLoading,
}: ChecklistSidebarProps) {
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">
            {t('checklists.yourChecklists', 'Your Checklists')}
          </h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-900">
          {t('checklists.yourChecklists', 'Your Checklists')}
        </h2>
      </div>

      {checklists.length === 0 ? (
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <ListChecks className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm text-gray-600 mb-3">
            {t('checklists.noChecklists', 'No checklists yet')}
          </p>
          <button
            onClick={onCreateClick}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t('checklists.createFirstChecklist', 'Create your first checklist')}
          </button>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {checklists.map((checklist, index) => {
            const progress = calculateProgress(checklist.itemCount, checklist.completedCount);
            const isSelected = selectedId === checklist.id;

            return (
              <button
                key={checklist.id}
                onClick={() => onSelect(checklist.id)}
                className={`
                  w-full p-4 text-left transition-all duration-300 group
                  hover:bg-gray-50 hover:shadow-sm
                  ${isSelected ? 'bg-primary-50 border-l-4 border-l-primary-600' : ''}
                  animate-fade-in
                `}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate transition-colors ${
                      isSelected ? 'text-primary-700' : 'text-gray-900 group-hover:text-primary-600'
                    }`}>
                      {checklist.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {checklist.completedCount} / {checklist.itemCount} {t('checklists.items', 'items')}
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                    isSelected ? 'text-primary-600 translate-x-1' : 'text-gray-400 group-hover:translate-x-1'
                  }`} />
                </div>

                {checklist.itemCount > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{t('checklists.progress', 'Progress')}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          progress === 100 ? 'bg-green-500' : 'bg-primary-600'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
